import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { setMainContentLoaded, selectProducts } from '../../brand/ducks/index';
import useResources from '../../resource/use-resources';
import { H2, LabelRegular } from '@comeon/ui';
import breakpoint from '../../../utils/style-utils';
import { useTypedTranslations } from '../../translation/use-translations';
import ProductNav from '../../misc/product-nav';

const resourceKey = 'SPORTSBOOK_MAINTENANCE_MAIN_IMAGE';

enum TextKeys {
	PRODUCTS_AD_LABEL = 'SPORTSBOOK_MAINTENANCE_PRODUCTS_AD_LABEL'
}

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 560px;
	padding: 72px 24px 56px;
	margin: 0 auto;
	gap: 12px;

	${breakpoint.lg`
		gap: 20px;
	`}

	text-align: center;
`;

const MainImg = styled.img`
	width: 115px;
	height: 100px;
`;

const Header = styled(H2)`
	font-size: 36px;
	font-weight: 600;
	line-height: 44px;
`;

const Label = styled(LabelRegular)`
	margin: 0;
	font-size: 17px;
	font-weight: 400;
	line-height: 24px;
`;

const Description = styled(Label)``;

const ProductsAd = styled(Label)`
	padding-top: 12px;
	border-top: 1px solid var(--sportsbook-maintainance-border-color, var(--sportsbook-border-secondary));
`;

const productClassName = 'maintainance-product-icon';

const StyledProductNav = styled(ProductNav)`
	margin-top: 18px;

	.${productClassName} > div:first-child {
		background-color: var(--sportsbook-maintainance-product-bg-color);
		svg {
			fill: var(--sportsbook-maintainance-product-icon-color);
		}
	}

	.${productClassName} > label {
		font-weight: 700;
		color: var(--sportsbook-maintainance-product-label-color);
	}

	// splashtech product
	button > div:first-child {
		margin: 0 20px;
		background-color: var(--sportsbook-maintainance-product-bg-color);
		svg {
			fill: var(--sportsbook-maintainance-product-icon-color);
		}
	}

	label {
		font-weight: 700;
		color: var(--sportsbook-maintainance-product-label-color);
	}
`;

const productsToIgnore = ['live-betting', 'sportsbook'];

type Props = {
	title: string;
	description?: string;
	disableProductMenu?: boolean;
};

const SportsbookUnavailable: React.FC<Props> = ({ title, description, disableProductMenu }) => {
	const { isLoading: isLoadingResources, resources } = useResources([resourceKey]);
	const { isLoadingTranslations, translations } = useTypedTranslations(TextKeys);
	const dispatch = useDispatch();
	const products = useSelector<unknown, Array<string>>(selectProducts);

	const filteredProducts = useMemo(() => products.filter(product => !productsToIgnore.includes(product)), [products]);
	const hasCrossSellMenu = filteredProducts.length > 0 && !disableProductMenu;

	useEffect(() => {
		if (!isLoadingTranslations && !isLoadingResources) {
			dispatch(setMainContentLoaded());
		}
	}, [dispatch, isLoadingTranslations, isLoadingResources]);

	if (isLoadingTranslations || isLoadingResources) {
		return null;
	}

	return (
		<Wrapper>
			<MainImg src={resources[resourceKey]} />
			<Header>{title}</Header>
			<Description my={1}>{description}</Description>
			{hasCrossSellMenu && (
				<>
					<ProductsAd my={1}>{translations[TextKeys.PRODUCTS_AD_LABEL]}</ProductsAd>
					<StyledProductNav products={filteredProducts} productClassName={productClassName} />
				</>
			)}
		</Wrapper>
	);
};

export default SportsbookUnavailable;
